import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#countFrame !== null) cancelAnimationFrame(this.#countFrame);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @type {number | null} */
  #countFrame = null;

  /**
   * Restarts the text swap animation. Removing the class and forcing a reflow
   * is what lets it replay — re-adding alone is a no-op while it is still set.
   */
  #flagUpdate = () => {
    this.classList.remove('product-inventory--updating');
    void this.offsetWidth;
    this.classList.add('product-inventory--updating');
  };

  /**
   * Counts the displayed figure from one value to another.
   * @param {number} from - The outgoing quantity.
   * @param {number} to - The incoming quantity.
   */
  #countTo = (from, to) => {
    const target = this.querySelector('[data-inventory-count]');
    if (!target) return;

    if (this.#countFrame !== null) cancelAnimationFrame(this.#countFrame);

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !Number.isFinite(from) || !Number.isFinite(to) || from === to) {
      if (Number.isFinite(to)) target.textContent = String(to);
      return;
    }

    const duration = 450;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // easeOutCubic — most of the travel up front, so it reads as settling.
      const eased = 1 - Math.pow(1 - progress, 3);
      target.textContent = String(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        this.#countFrame = requestAnimationFrame(step);
      } else {
        this.#countFrame = null;
        target.textContent = String(to);
      }
    };

    this.#countFrame = requestAnimationFrame(step);
  };

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        // Read the outgoing figure before morph replaces the children, so the
        // count can travel from the old value rather than snapping to the new.
        const previous = Number(this.dataset.inventoryQuantity);
        const next = Number(newInventory.dataset.inventoryQuantity);

        morph(this, newInventory, { childrenOnly: true });

        // morph only touches children, so these stay on the host element.
        this.dataset.inventoryQuantity = newInventory.dataset.inventoryQuantity ?? '';
        this.dataset.inventoryStatus = newInventory.dataset.inventoryStatus ?? '';

        // The bar fills via a CSS transition off this custom property. Setting
        // it after morph means the browser has a previous value to animate
        // from; setting it during morph would be the element's first paint.
        const level = newInventory.style.getPropertyValue('--inventory-level');
        if (level) this.style.setProperty('--inventory-level', level);

        this.#flagUpdate();
        this.#countTo(previous, next);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
